/**
 * RealStockChart — ADV-DATA-PHASE2 (candlestick + technical-level overlays).
 *
 * Renders a REAL price chart for a symbol from existing backend data only:
 *   * OHLCV bars  : GET /api/v1/quote/{symbol}/bars?timeframe=1D|1W|1M|1Y  ({t,o,h,l,c,v})
 *   * TA levels   : GET /api/v1/ta/levels/{symbol}  (vwap, prior-day H/L/C, premarket H/L,
 *                   range floor/ceiling, breakout trigger, invalidation level)
 *
 * Modes: Candles (recharts floating-bar custom shape — no new dependency) or Line.
 * OHLC tooltip, optional volume panel, and toggle-able numeric level overlays.
 * NO fabricated candles / NO fabricated levels: missing data → honest compact states.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from 'recharts';
import { apiV1Base } from '../lib/apiConfig';
import { reconcileDetailPrice, type ReconcileResult } from '../lib/priceReconcile'; // PRICERECON-001

interface OHLCV { t: string; o: number; h: number; l: number; c: number; v: number; }
interface ChartState {
  loading: boolean; error: string | null; bars: OHLCV[];
  source: string | null; status: string | null; lastUpdated: string | null; session: string | null;
}
type Levels = Record<string, number | null | undefined> | null;

const _INTRADAY = new Set(['1D', '1W']);
type ChartMode = 'candles' | 'line';

// Technical level overlays (only numeric values are drawn; never fabricated).
// ADV-MKT-PREDEPLOY-002 (Track B): colours upgraded for AA-readable contrast on the dark
// chart; `tier` drives rendering emphasis. Breakout = dominant non-red; invalidation =
// distinct readable red; all others = muted reference levels (legend-labelled, not inline).
type LevelTier = 'ref' | 'breakout' | 'invalidation';
const _LEVELS: Array<{ key: string; label: string; color: string; tier: LevelTier }> = [
  { key: 'vwap', label: 'VWAP', color: '#a5b4fc', tier: 'ref' },            // indigo-300
  { key: 'prior_day_high', label: 'PDH', color: '#7dd3fc', tier: 'ref' },   // sky-300
  { key: 'prior_day_low', label: 'PDL', color: '#7dd3fc', tier: 'ref' },
  { key: 'prior_day_close', label: 'PDC', color: '#cbd5e1', tier: 'ref' },  // slate-300
  { key: 'premarket_high', label: 'PMH', color: '#fcd34d', tier: 'ref' },   // amber-300
  { key: 'premarket_low', label: 'PML', color: '#fcd34d', tier: 'ref' },
  { key: 'range_floor', label: 'Floor', color: '#cbd5e1', tier: 'ref' },
  { key: 'range_ceiling', label: 'Ceil', color: '#cbd5e1', tier: 'ref' },
  { key: 'breakout_trigger', label: 'Breakout', color: '#34d399', tier: 'breakout' },      // emerald-400, dominant, non-red
  { key: 'invalidation_level', label: 'Invalid', color: '#fb7185', tier: 'invalidation' }, // rose-400, readable red
];

// Collision-free, never-right-clipped level tag: a dark-backed high-contrast pill anchored
// in the LEFT inside-gutter at the line's y. `place` nudges above/below the line so the two
// emphasised tags (breakout/invalidation) never overlap even at near-equal prices.
function renderLevelTag(p: any, text: string, color: string, dominant: boolean, place: 'top' | 'below') {
  const vb = (p && p.viewBox) || {};
  const vx = Number(vb.x) || 0;
  const vy = Number(vb.y) || 0;
  const fs = dominant ? 11 : 10;
  const padX = 5;
  const h = fs + 7;
  const w = Math.max(44, text.length * fs * 0.6 + padX * 2);
  const x = vx + 6;
  const y = vy + (place === 'below' ? h * 0.6 : -h * 0.6);
  return (
    <g pointerEvents="none">
      <rect x={x} y={y - h / 2} width={w} height={h} rx={3}
            fill="rgba(2,6,23,0.92)" stroke={color} strokeWidth={dominant ? 1.4 : 1} />
      <text x={x + padX} y={y + fs * 0.34} fontSize={fs} fontWeight={dominant ? 800 : 700}
            fill={color} letterSpacing="0.04em">{text}</text>
    </g>
  );
}

function _fmtTime(t: string, tf: string): string {
  try {
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) return '';
    return _INTRADAY.has(tf)
      ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
      : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

// Candle glyph for a recharts floating Bar whose dataKey returns [low, high].
// recharts gives us the pixel rect for that range (y=top@high, height=span); we
// position the open/close body inside it via a linear price→pixel scale.
function Candle(props: any) {
  const { x, width, y, height, payload } = props;
  if (!payload || typeof payload.o !== 'number' || typeof payload.h !== 'number') return null;
  const { o, h, l, c } = payload;
  const span = h - l;
  const color = c >= o ? '#059669' : '#e11d48';
  const cx = x + width / 2;
  if (!(span > 0) || !(height > 0)) {
    return <line x1={x} y1={y} x2={x + width} y2={y} stroke={color} strokeWidth={1} />;
  }
  const ppp = height / span;
  const bodyTop = y + (h - Math.max(o, c)) * ppp;
  const bodyH = Math.max(1, Math.abs(c - o) * ppp);
  const bw = Math.max(1.5, width * 0.6);
  return (
    <g>
      <line x1={cx} y1={y} x2={cx} y2={y + height} stroke={color} strokeWidth={1} />
      <rect x={cx - bw / 2} y={bodyTop} width={bw} height={bodyH} fill={color} stroke={color} />
    </g>
  );
}

function ChartTooltip({ active, payload }: any) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  const row = (k: string, v: string) => (
    <div className="flex justify-between gap-3"><span className="text-[var(--muted-text)]">{k}</span><span className="font-mono">{v}</span></div>
  );
  return (
    <div data-testid="markets-chart-tooltip" className="rounded-md border border-[var(--card-border)] bg-[var(--card-bg)] px-2 py-1 text-[10px] shadow">
      <div className="font-bold mb-0.5">{p.time}</div>
      {typeof p.o === 'number' && row('O', `$${p.o.toFixed(2)}`)}
      {typeof p.h === 'number' && row('H', `$${p.h.toFixed(2)}`)}
      {typeof p.l === 'number' && row('L', `$${p.l.toFixed(2)}`)}
      {typeof p.c === 'number' && row('C', `$${p.c.toFixed(2)}`)}
      {typeof p.volume === 'number' && row('Vol', p.volume.toLocaleString())}
    </div>
  );
}

export default function RealStockChart({ symbol, timeframe, headerPrice, headerStale, headerQuality, onReconcile }: {
  symbol: string; timeframe: string;
  // PRICERECON-001 — the detail-view header quote, so the chart can reconcile the four price paths
  // (header / candles / tooltip / C4 levels) and surface an honest verdict up to the detail view.
  headerPrice?: number | null; headerStale?: boolean; headerQuality?: string | null;
  onReconcile?: (r: ReconcileResult) => void;
}) {
  const [state, setState] = useState<ChartState>({
    loading: true, error: null, bars: [], source: null, status: null, lastUpdated: null, session: null,
  });
  const [levels, setLevels] = useState<Levels>(null);
  const [mode, setMode] = useState<ChartMode>('candles');
  const [showLevels, setShowLevels] = useState(true);
  const [showVolume, setShowVolume] = useState(true);
  const aliveRef = useRef(true);

  // bars (per symbol + timeframe)
  useEffect(() => {
    aliveRef.current = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    const url = `${apiV1Base()}/quote/${encodeURIComponent(symbol)}/bars?timeframe=${encodeURIComponent(timeframe)}`;
    fetch(url, { cache: 'no-store' })
      .then(async (r) => {
        if (!aliveRef.current) return;
        if (!r.ok) { setState((s) => ({ ...s, loading: false, error: `HTTP ${r.status}`, bars: [] })); return; }
        const body = await r.json();
        const d = body?.data ?? body ?? {};
        setState({
          loading: false, error: null, bars: Array.isArray(d.bars) ? d.bars : [],
          source: d.source ?? d.provider ?? null, status: d.status ?? null,
          lastUpdated: d.last_updated ?? null, session: d.market_session ?? null,
        });
      })
      .catch((e) => { if (aliveRef.current) setState((s) => ({ ...s, loading: false, error: String(e?.message || e), bars: [] })); });
    return () => { aliveRef.current = false; };
  }, [symbol, timeframe]);

  // technical levels (per symbol)
  useEffect(() => {
    let alive = true;
    setLevels(null);
    fetch(`${apiV1Base()}/ta/levels/${encodeURIComponent(symbol)}`, { cache: 'no-store' })
      .then(async (r) => { if (!alive) return; if (!r.ok) { setLevels(null); return; } const b = await r.json(); setLevels((b?.data ?? b ?? null) as Levels); })
      .catch(() => { if (alive) setLevels(null); });
    return () => { alive = false; };
  }, [symbol]);

  // PRICERECON-001 — reconcile the four price paths from the chart's own bars + levels + the
  // header quote. Display-only; no gating/integrity logic touched. Surfaced up via onReconcile.
  const reconcile = React.useMemo(() => reconcileDetailPrice({
    headerPrice: headerPrice ?? null,
    headerStale: !!headerStale,
    headerQuality: headerQuality ?? null,
    bars: state.bars,
    levels: levels as any,
  }), [headerPrice, headerStale, headerQuality, state.bars, levels]);
  useEffect(() => { if (!state.loading) onReconcile?.(reconcile); }, [reconcile, state.loading, onReconcile]);

  const wrap = 'bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 min-h-[320px] h-[560px] w-full';

  if (state.loading) {
    return (
      <div data-testid="markets-chart-loading" className={`${wrap} flex items-center justify-center text-[var(--muted-text)] font-black uppercase italic`}>
        <span className="text-sm opacity-60">Loading {symbol} {timeframe} chart…</span>
      </div>
    );
  }

  const unavailable = !!state.error || (state.status || '').toUpperCase() === 'UNAVAILABLE' || state.bars.length === 0;
  if (unavailable) {
    return (
      <div data-testid="markets-chart-unavailable" className={`${wrap} flex flex-col items-center justify-center text-[var(--muted-text)] font-black uppercase italic space-y-2`}>
        <div className="text-4xl opacity-20">{symbol} {timeframe}</div>
        <div className="text-sm opacity-60">Chart data unavailable for this symbol.</div>
      </div>
    );
  }

  const data = state.bars.map((b) => ({
    time: _fmtTime(b.t, timeframe),
    o: typeof b.o === 'number' ? b.o : null,
    h: typeof b.h === 'number' ? b.h : null,
    l: typeof b.l === 'number' ? b.l : null,
    c: typeof b.c === 'number' ? b.c : null,
    close: typeof b.c === 'number' ? b.c : null,
    range: (typeof b.l === 'number' && typeof b.h === 'number') ? [b.l, b.h] : null,
    volume: typeof b.v === 'number' ? b.v : 0,
  }));

  // numeric levels actually present (never fabricated)
  const activeLevels = levels
    ? _LEVELS.filter((L) => typeof levels[L.key] === 'number' && Number.isFinite(levels[L.key] as number))
    : [];

  // y-domain spans bar highs/lows AND any visible numeric levels so overlays aren't clipped
  const highs = data.map((d) => d.h).filter((x): x is number => x !== null);
  const lows = data.map((d) => d.l).filter((x): x is number => x !== null);
  let lo = Math.min(...lows);
  let hi = Math.max(...highs);
  if (showLevels) {
    for (const L of activeLevels) { const v = levels![L.key] as number; lo = Math.min(lo, v); hi = Math.max(hi, v); }
  }
  const pad = (hi - lo) * 0.05 || 1;
  const trendUp = data.length >= 2 && data[0].close != null && data[data.length - 1].close != null
    ? (data[data.length - 1].close as number) >= (data[0].close as number) : true;
  const lineColor = trendUp ? '#059669' : '#e11d48';
  const statusU = (state.status || '').toUpperCase();
  const lastUpd = state.lastUpdated ? new Date(state.lastUpdated) : null;
  const maxVol = Math.max(...data.map((d) => d.volume), 1);

  const btn = (activeOn: boolean) =>
    `px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border ${
      activeOn ? 'bg-[var(--app-text)] text-[var(--card-bg)] border-[var(--app-text)]'
               : 'text-[var(--muted-text)] border-[var(--card-border)] hover:text-[var(--app-text)]'}`;

  return (
    <div data-testid="markets-chart" data-symbol={symbol} data-timeframe={timeframe} data-chart-mode={mode} className={`${wrap} flex flex-col`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">
          {symbol} · {timeframe} · {mode === 'candles' ? 'OHLC' : 'Close'}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <button data-testid="chart-mode-candles" onClick={() => setMode('candles')} className={btn(mode === 'candles')}>Candles</button>
            <button data-testid="chart-mode-line" onClick={() => setMode('line')} className={btn(mode === 'line')}>Line</button>
          </div>
          <button data-testid="chart-toggle-levels" onClick={() => setShowLevels((v) => !v)} className={btn(showLevels)}>Levels</button>
          <button data-testid="chart-toggle-volume" onClick={() => setShowVolume((v) => !v)} className={btn(showVolume)}>Volume</button>
          {statusU && (
            <span data-testid="markets-chart-status"
                  className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border ${
                    statusU === 'LIVE' ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30'
                    : statusU === 'STALE' ? 'bg-amber-500/10 text-amber-700 border-amber-500/30'
                    : 'bg-slate-500/10 text-slate-600 border-slate-500/30'}`}>
              {statusU}
            </span>
          )}
        </div>
      </div>
      {/* PRICERECON-001 — honest note when the candle/tooltip series spans a possible split
          (raw/unadjusted): values on the chart may mix pre/post-split and are not a clean basis. */}
      {reconcile.splitContaminated && (
        <div data-testid="chart-split-warning"
             className="mb-2 px-3 py-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wide">
          Chart basis unreconciled — series spans a possible split (raw/unadjusted); candle & tooltip
          values may mix pre/post-split. Verify price externally.
        </div>
      )}

      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 4, right: 44, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--card-border)" opacity={0.4} />
            <XAxis dataKey="time" tick={{ fontSize: 9 }} minTickGap={48} stroke="var(--muted-text)" />
            <YAxis yAxisId="price" domain={[lo - pad, hi + pad]} tick={{ fontSize: 9 }} width={48}
                   stroke="var(--muted-text)" tickFormatter={(v) => `$${Number(v).toFixed(0)}`} allowDecimals />
            <YAxis yAxisId="vol" orientation="right" hide domain={[0, maxVol * 4]} />
            <Tooltip content={<ChartTooltip />} />
            {showVolume && (
              <Bar yAxisId="vol" dataKey="volume" fill="var(--muted-text)" opacity={0.16} isAnimationActive={false} />
            )}
            {mode === 'candles' ? (
              <Bar yAxisId="price" dataKey="range" shape={<Candle />} isAnimationActive={false} legendType="none" />
            ) : (
              <Line yAxisId="price" type="monotone" dataKey="close" stroke={lineColor} strokeWidth={1.6}
                    dot={false} isAnimationActive={false} connectNulls />
            )}
            {showLevels && activeLevels.map((L) => {
              const v = levels![L.key] as number;
              if (L.tier === 'breakout') {
                // Dominant: heaviest solid line + bold non-red "BREAKOUT $X.XX" pill (left-anchored).
                return (
                  <ReferenceLine key={L.key} yAxisId="price" y={v} stroke={L.color}
                                 strokeWidth={2.6} ifOverflow="extendDomain"
                                 label={(p: any) => renderLevelTag(p, `BREAKOUT $${v.toFixed(2)}`, L.color, true, 'top')} />
                );
              }
              if (L.tier === 'invalidation') {
                // Secondary emphasis: distinct dashed readable-red + "INVALIDATION $X.XX" pill (below the line).
                return (
                  <ReferenceLine key={L.key} yAxisId="price" y={v} stroke={L.color}
                                 strokeWidth={1.8} strokeDasharray="6 3" ifOverflow="extendDomain"
                                 label={(p: any) => renderLevelTag(p, `INVALIDATION $${v.toFixed(2)}`, L.color, false, 'below')} />
                );
              }
              // Reference levels: muted thin dashed line, NO inline label (identity + value live in
              // the compact legend below) -> eliminates right-edge stacking, overlap and clipping.
              return (
                <ReferenceLine key={L.key} yAxisId="price" y={v} stroke={L.color}
                               strokeDasharray="2 3" strokeOpacity={0.5} ifOverflow="extendDomain" />
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* compact level legend (numeric levels only; honest unavailable text) */}
      {showLevels && (
        activeLevels.length > 0 ? (
          <div data-testid="markets-chart-levels" className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[9px] font-bold uppercase tracking-widest">
            {activeLevels.map((L) => (
              <span key={L.key} data-level={L.key} data-tier={L.tier} style={{ color: L.color }}
                    className={L.tier === 'breakout' ? 'text-[10px] font-black' : (L.tier === 'invalidation' ? 'font-black' : '')}>
                {L.tier === 'breakout' ? 'BREAKOUT' : L.tier === 'invalidation' ? 'INVALIDATION' : L.label} {(levels![L.key] as number).toFixed(2)}
              </span>
            ))}
          </div>
        ) : (
          <div data-testid="markets-chart-levels-unavailable" className="mt-2 text-[9px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
            Technical levels unavailable.
          </div>
        )
      )}

      <div data-testid="markets-chart-source" className="mt-2 text-[9px] font-bold uppercase tracking-widest text-[var(--muted-text)] flex items-center justify-between">
        <span>Source: {state.source || '—'}{state.session ? ` · ${state.session}` : ''}</span>
        <span>{lastUpd && !Number.isNaN(lastUpd.getTime()) ? `Updated ${lastUpd.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}</span>
      </div>
    </div>
  );
}
